import { Handlers } from '../../types/actions/types';
import { CONSTANTS, UnlockAICASourcePage } from './constants';
import {
    AddToWatchlistModalPayload,
    BureauAndKYCConsentLetterData,
    CustomFilterDrawerState,
    ColumnCustomizationState,
    DealApplicationDrawerState,
    DealDrawerState,
    DealFilters,
    EngagementLetterData,
    NDAData,
    OnePagerData,
} from './reducer';
import {
    CompanyUpdatePayload,
    DealUpdatePayload,
    FetchAllInvestmentPreferencesPayload,
    FetchDealMetricsPayload,
    InvestorAccessTypePermissionServicePayload,
    InvestorDealUpdatePayload,
    getAllDealsService,
    getAllCompanyDealsService,
} from './service';
import { MasterData } from '../../common/DealApplicationDrawer/customHooks/useDADataTransformations';
import { JAVA_API_STATUS, SOMETHING_WRONG } from '../../utils/axios-interceptor';
import { toast } from 'react-toastify';
import { filter } from 'lodash';
import { SortConfig } from '../../utils/constants/commonConstants';

export function fetchInvestorConfig(payload: FetchInvestorConfigPayload) {
    return {
        type: CONSTANTS.FETCH_INVESTOR_CONFIG,
        payload,
    };
}

export function fetchLenderConfig(payload: FetchInvestorConfigPayload) {
    return {
        type: CONSTANTS.FETCH_LENDER_CONFIG,
        payload,
    };
}

export function fetchPortfolioData(payload: FetchPortfolioDataPayload) {
    return {
        type: CONSTANTS.FETCH_PORTFOLIO_DATA,
        payload,
    };
}

export function updateUntaggedMuFileNumber(uploadCount: number) {
    return {
        type: CONSTANTS.UPDATE_UNTAGGED_MU_FILE_NUMBER,
        payload: uploadCount,
    };
}

export function updateNeedInfoFileNumber(uploadCount: number) {
    return {
        type: CONSTANTS.UPDATE_NEED_INFO_FILE_NUMBER,
        payload: uploadCount,
    };
}

export function fetchOwners(payload: GetOwnerDataPayload) {
    return {
        type: CONSTANTS.FETCH_OWNER_DATA,
        payload,
    };
}

export function fetchPartnerData(payload: FetchOrgBasedUsersPayload) {
    return {
        type: CONSTANTS.FETCH_PARTNER_DATA,
        payload,
    };
}

export function fetchInvestorAccessTypePermissions(payload: InvestorAccessTypePermissionServicePayload, handlers: Handlers) {
    return {
        type: CONSTANTS.FETCH_INVESTOR_PERMISSIONS,
        payload,
        handlers,
    };
}

export function fetchSingleCompanyDetails(payload, handlers) {
    return {
        type: CONSTANTS.FETCH_SINGLE_COMPANY_DETAILS,
        payload,
        handlers,
    };
}

export function fetchCompanyComments(payload, handlers) {
    return {
        type: CONSTANTS.FETCH_COMPANY_COMMENTS,
        payload,
        handlers,
    };
}

export function initiateReportGeneration(payload: InitiateReportGenerationPayload) {
    return {
        type: CONSTANTS.INITIATE_REPORT_GENERATION,
        payload,
    };
}

export function inviteCompany(payload: InviteCompanyPayload, handlers) {
    return {
        type: CONSTANTS.INVITE_COMPANY,
        payload,
        handlers,
    };
}

export const inviteCompanyV2 = (payload: InviteCompanyPayloadV2, handlers) => {
    return {
        type: CONSTANTS.INVITE_COMPANY_V2,
        payload,
        handlers,
    };
};

export function changeSelectedFetchType(payload: any) {
    return {
        type: CONSTANTS.SET_PORTFOLIO_SELECTED_FETCH_TYPE,
        payload,
    };
}

export function changePortfolioPageNum(payload: any) {
    return {
        type: CONSTANTS.SET_PORTFOLIO_PAGE_NUMBER,
        payload,
    };
}

export function setShowUpgradeToScan(payload: string) {
    return {
        type: CONSTANTS.SET_SHOW_UPGRADE_TO_SCAN,
        payload,
    };
}

export function setShowRefreshReport(payload: boolean) {
    return {
        type: CONSTANTS.SET_SHOW_REFRESH_REPORT,
        payload,
    };
}

export function setShowUnlockAicaConfirmation(payload: any) {
    return {
        type: CONSTANTS.SET_SHOW_UNLOCK_AICA_CONFIRMATION,
        payload,
    };
}

export function updateCompany(payload: CompanyUpdatePayload, handlers?: Handlers) {
    return {
        type: CONSTANTS.UPDATE_COMPANY,
        payload,
        handlers,
    };
}

export function createDeal(payload: any, handlers) {
    return {
        type: CONSTANTS.CREATE_DEAL,
        payload,
        handlers,
    };
}

export function setDealDrawer(payload: DealDrawerState) {
    return {
        type: CONSTANTS.SET_DEAL_DRAWER,
        payload,
    };
}

export function setDealTermsLoggingDrawerPrefilledStatus(status: string | null) {
    return {
        type: CONSTANTS.SET_DEAL_TERMS_LOGGING_DRAWER_PREFILLED_STATUS,
        payload: status,
    };
}

export function setDealFilterDrawer(payload: DealDrawerState) {
    return {
        type: CONSTANTS.SET_DEAL_FILTER_DRAWER,
        payload,
    };
}

export function getMetricsForDealCreation(payload: FetchDealMetricsPayload, handlers) {
    return {
        type: CONSTANTS.METRICS_FOR_DEAL_CREATION,
        payload,
        handlers,
    };
}

export function getPecMatchingData(payload: any, handlers) {
    return {
        type: CONSTANTS.PEC_MATCHING,
        payload,
        handlers,
    };
}

const setIsFetchingDeals = (payload: boolean) => ({
    type: CONSTANTS.SET_IS_FETCHING_DEALS,
    payload,
});

const fetchDealsSuccess = (payload: any) => ({
    type: CONSTANTS.FETCH_DEALS_SUCCESS,
    payload,
});

export const clearDealsData = () => ({
    type: CONSTANTS.CLEAR_DEALS_DATA,
});

export const getAllDeals = (payload: {
    dealStatuses?: string[];
    dealProposerId: string;
    pageNumber: number;
    pageSize: number;
    ownerUserId: string;
    dealBeneficiaryId?: string;
    sortConfig?: SortConfig;
    filters?: Record<string, any>;
}) => {
    return async (dispatch: any) => {
        try {
            dispatch(setIsFetchingDeals(true));

            const response = await getAllDealsService({
                ...payload,
                dealStatuses: payload.dealStatuses ?? [],
                filters: {
                    ...payload.filters,
                },
                ...(payload.sortConfig?.direction && { sortConfig: payload.sortConfig }),
            });

            // Check responseBody.responseData first (business logic level)
            const responseBodyData = response?.data?.responseBody?.responseData;
            if (responseBodyData?.responseCode === JAVA_API_STATUS.ERROR) {
                dispatch(clearDealsData());
                toast.error(responseBodyData?.responseMessage || SOMETHING_WRONG);
                return;
            }

            // Check outer responseData (request processing level)
            const responseData = response?.data?.responseData;
            if (responseData?.responseCode === JAVA_API_STATUS.SUCCESS) {
                dispatch(fetchDealsSuccess(response.data.responseBody ?? {}));
            } else {
                dispatch(clearDealsData());
                toast.error(responseData?.responseMessage || SOMETHING_WRONG);
            }
        } catch (error) {
            console.error('Error fetching deals:', error);
            dispatch(clearDealsData());
            toast.error(SOMETHING_WRONG);
        } finally {
            dispatch(setIsFetchingDeals(false));
        }
    };
};

export const getAllCompanyDeals = (payload: {
    dealStatuses?: string[];
    pageNumber: number;
    pageSize: number;
    ownerUserId: string;
    dealBeneficiaryId?: string;
    sortConfig?: SortConfig;
    filters?: Record<string, any>;
}) => {
    return async (dispatch: any) => {
        try {
            dispatch(setIsFetchingDeals(true));

            const response = await getAllCompanyDealsService({
                ...payload,
                dealStatuses: payload.dealStatuses ?? [],
                filters: {
                    ...payload.filters,
                },
                ...(payload.sortConfig?.direction && { sortConfig: payload.sortConfig }),
            });

            // Check responseBody.responseData first (business logic level)
            const responseBodyData = response?.data?.responseBody?.responseData;
            if (responseBodyData?.responseCode === JAVA_API_STATUS.ERROR) {
                dispatch(clearDealsData());
                toast.error(responseBodyData?.responseMessage || SOMETHING_WRONG);
                return;
            }

            // Check outer responseData (request processing level)
            if (response.data.responseData.responseCode === JAVA_API_STATUS.SUCCESS) {
                dispatch(fetchDealsSuccess(response.data.responseBody));
            } else {
                dispatch(clearDealsData());
                toast.error(response.data.responseData.responseMessage || SOMETHING_WRONG);
            }
        } catch (error) {
            console.error('Error fetching company deals:', error);
            dispatch(clearDealsData());
            toast.error(SOMETHING_WRONG);
        } finally {
            dispatch(setIsFetchingDeals(false));
        }
    };
};

export function getAllLenderDeals(payload: {
    dealStatuses?: string[];
    dealProposerId: string;
    pageNumber: number;
    pageSize: number;
    ownerUserId: string;
    sortConfig?: SortConfig;
    dealBeneficiaryId?: string;
    filters?: Record<string, any>;
}) {
    return {
        type: CONSTANTS.GET_ALL_LENDER_DEALS,
        payload,
    };
}

export function getSpecificLenderDeals(payload: {
    dealStatuses?: string[];
    dealProposerId: string;
    pageNumber: number;
    pageSize: number;
    ownerUserId: string;
    sortConfig?: SortConfig;
    filters?: Record<string, any>;
}) {
    return {
        type: CONSTANTS.GET_SPECIFIC_LENDER_DEALS,
        payload,
    };
}

export function updateDeal(payload: DealUpdatePayload | any, handlers?: Handlers) {
    return {
        type: CONSTANTS.UPDATE_DEAL,
        payload,
        handlers,
    };
}

export function updateInvestorDeal(payload: InvestorDealUpdatePayload, handlers?: Handlers) {
    return {
        type: CONSTANTS.UPDATE_INVESTOR_DEAL,
        payload,
        handlers,
    };
}

export function updateDealLastStatusUpdateTime(payload: { dealId: string; investorDealId?: string }) {
    return {
        type: CONSTANTS.UPDATE_DEAL_LAST_STATUS_UPDATE_TIME,
        payload: {
            ...payload,
            customFields: {
                lastStatusUpdateTime: Date.now(),
            },
        },
    };
}

export function getDealData(payload: { dealId?: string; dealBeneficiaryId?: string }, handlers) {
    return {
        type: CONSTANTS.GET_DEAL_DATA,
        payload,
        handlers,
    };
}

export function getLenderDealData(payload: { dealId: string }, handlers) {
    return {
        type: CONSTANTS.GET_LENDER_DEAL_DATA,
        payload,
        handlers,
    };
}

export function getInvestmentPreference(payload: { investmentPreferenceId: string }, handlers) {
    return {
        type: CONSTANTS.GET_INVESTMENT_PREFERENCE,
        payload,
        handlers,
    };
}

export function getAllInvestmentPreferences(payload: FetchAllInvestmentPreferencesPayload, handlers) {
    return {
        type: CONSTANTS.GET_ALL_INVESTMENT_PREFERENCES,
        payload,
        handlers,
    };
}

export function updateInvestmentPreference(payload: any, handlers) {
    return {
        type: CONSTANTS.UPDATE_INVESTMENT_PREFERENCE,
        payload,
        handlers,
    };
}

export function createInvestmentPreference(payload: any, handlers) {
    return {
        type: CONSTANTS.CREATE_INVESTMENT_PREFERENCE,
        payload,
        handlers,
    };
}

export function setShowUnlockAicaAndAddToLeads(payload: ShowUnlockAicaAndAddToLeadsPayload) {
    return {
        type: CONSTANTS.SET_SHOW_UNLOCK_AICA_AND_ADD_TO_LEADS,
        payload,
    };
}

export function setShowInviteUserModal(payload: boolean) {
    return {
        type: CONSTANTS.SET_SHOW_INVITE_USER,
        payload,
    };
}

export function setShowGlobalSearch(payload: boolean) {
    return {
        type: CONSTANTS.SET_SHOW_GLOBAL_SEARCH,
        payload,
    };
}

export function setAddToWatchlistModal(payload: AddToWatchlistModalPayload) {
    return {
        type: CONSTANTS.SET_ADD_TO_WATCHLIST_MODAL,
        payload,
    };
}

export function setShowInviteUserWithCallback(payload: { show: boolean; onSuccess: () => void }) {
    return {
        type: CONSTANTS.SET_SHOW_INVITE_USER_WITH_CALLBACK,
        payload,
    };
}

export function setCustomFilterDrawer(payload: CustomFilterDrawerState) {
    return {
        type: CONSTANTS.SET_CUSTOM_FILTER_DRAWER,
        payload,
    };
}

// Column Customization Actions
export function openColumnCustomizationDrawer(payload: { sourceKey: string; selectedColumns: string[] }) {
    return {
        type: CONSTANTS.OPEN_COLUMN_CUSTOMIZATION_DRAWER,
        payload,
    };
}

export function closeColumnCustomizationDrawer() {
    return {
        type: CONSTANTS.CLOSE_COLUMN_CUSTOMIZATION_DRAWER,
    };
}

export function setColumnCustomizationState(payload: Partial<ColumnCustomizationState>) {
    return {
        type: CONSTANTS.SET_COLUMN_CUSTOMIZATION_STATE,
        payload,
    };
}

export function updateSelectedColumns(payload: { selectedColumns: string[] }) {
    return {
        type: CONSTANTS.UPDATE_SELECTED_COLUMNS,
        payload,
    };
}

export function updateTempSelectedColumns(payload: { tempSelectedColumns: string[] }) {
    return {
        type: CONSTANTS.UPDATE_TEMP_SELECTED_COLUMNS,
        payload,
    };
}

export function applyColumnCustomization() {
    return {
        type: CONSTANTS.APPLY_COLUMN_CUSTOMIZATION,
    };
}

export function resetColumnCustomization(payload: { defaultColumns: string[] }) {
    return {
        type: CONSTANTS.RESET_COLUMN_CUSTOMIZATION,
        payload,
    };
}

export function setComposeEmailModal(payload: {
    show: boolean;
    selectedRequirements?: any[];
    orgName?: string;
    source?: string;
    orgId?: string;
    lenderId?: string;
    dealId?: string;
    partnerOrgId?: string;
}) {
    return {
        type: CONSTANTS.SET_COMPOSE_EMAIL_MODAL,
        payload,
    };
}

export function setCreateMandateModal(payload: { show: boolean }) {
    return {
        type: CONSTANTS.SET_CREATE_MANDATE_MODAL,
        payload,
    };
}

export function setConfigTableModal(payload: {
    show?: boolean;
    data?: {
        entity?: 'Deal' | 'Lead' | 'Mandate';
        actionType?: string;
        dropdownOption?: string;
        rowData?: any;
        source?: ('Deal' | 'Lead' | 'Mandate') | ('Deal' | 'Lead' | 'Mandate')[];
        dealId?: string;
        orgId?: string; // assesseeOrgId/dealBeneficiaryId for Lead data
        mandateId?: string; // mandateId if applicable
        onSuccess?: (data?: any) => void; // Called after successful API operations
        onCloseCallback?: () => void; // Called when modal is closed (cancel, X button, etc.)
    };
}) {
    return {
        type: CONSTANTS.SET_GENERIC_CONFIRMATION_MODAL,
        payload,
    };
}

export function triggerRequirementsRefresh() {
    return {
        type: CONSTANTS.TRIGGER_REQUIREMENTS_REFRESH,
    };
}

export function fetchTabLockStatus(payload: FetchTabLockStatusPayload) {
    return {
        type: CONSTANTS.FETCH_TAB_LOCK_STATUS,
        payload,
    };
}

export function setOnePagerData(payload: OnePagerData | { loading: boolean }) {
    return {
        type: CONSTANTS.SET_ONE_PAGER_DATA,
        payload,
    };
}

export function setEngagementLetterData(payload: EngagementLetterData | { loading: boolean }) {
    return {
        type: CONSTANTS.SET_ENGAGEMENT_LETTER_DATA,
        payload,
    };
}

export function setBureauKycConsentData(payload: BureauAndKYCConsentLetterData | { loading: boolean }) {
    return {
        type: CONSTANTS.SET_BUREAU_KYC_CONSENT_DATA,
        payload,
    };
}

export function handleDealApplicationDrawer(payload: DealApplicationDrawerState) {
    return {
        type: CONSTANTS.OPEN_DEAL_APPLICATION_DRAWER,
        payload,
    };
}

export function updateDealApplicationData(payload: {
    masterData?: MasterData;
    allProducts?: {
        label: string;
        value: string;
    }[];
    step?: number;
    disabledFields?: string[];
}) {
    return {
        type: CONSTANTS.UPDATE_DEAL_APPLICATION_DATA,
        payload,
    };
}

export function setModulePollingList(payload: string[]) {
    return {
        type: CONSTANTS.SET_MODULE_POLLING_LIST,
        payload,
    };
}

export function setTaskTrackerMap(payload: { [taskTrackerStep: string]: { status: string; dateCreated: number } }) {
    return {
        type: CONSTANTS.SET_TASK_TRACKER_MAP,
        payload,
    };
}

export function closeOnePager() {
    return {
        type: CONSTANTS.CLOSE_ONE_PAGER,
    };
}

export function closeEngagementLetter() {
    return {
        type: CONSTANTS.CLOSE_ENGAGEMENT_LETTER,
    };
}

export function closeBureauKycConsent() {
    return {
        type: CONSTANTS.CLOSE_BUREAU_KYC_CONSENT,
    };
}

export function setNDAData(payload: NDAData | { loading: boolean }) {
    return {
        type: CONSTANTS.SET_NDA_DATA,
        payload,
    };
}

export function closeNDA() {
    return {
        type: CONSTANTS.CLOSE_NDA,
    };
}

export const setShowManageTeamDrawer = (show: boolean) => ({
    type: CONSTANTS.SET_SHOW_MANAGE_TEAM_DRAWER,
    payload: show,
});

export const setEditUserModal = (show: boolean, userData: any = null, refetchUsers: boolean = false) => ({
    type: CONSTANTS.SET_EDIT_USER_MODAL,
    payload: { show, userData, refetchUsers },
});

export const setDealFilters = (filters: DealFilters) => ({
    type: CONSTANTS.SET_DEAL_FILTERS,
    payload: filters,
});

export const resetDealFilters = (payload: { allStatusesSelected?: boolean } = {}) => ({
    type: CONSTANTS.RESET_DEAL_FILTERS,
    payload,
});

export const initialiseCompaniesListFiltersConfig = (payload: {
    accessTier: string;
    headerConfigs: any;
    filtersList: Record<string, boolean>;
    dropdownOptions: Record<string, { label: string; value: string }[]>;
}) => ({
    type: CONSTANTS.INITIALISE_COMPANIES_LIST_FILTERS_CONFIG,
    payload,
});

export const setCompaniesListFilterValues = (payload) => {
    return {
        type: CONSTANTS.SELECT_COMPANIES_LIST_FILTER_VALUES,
        payload,
    };
};

export const resetCompaniesListFilterValues = () => {
    return {
        type: CONSTANTS.RESET_COMPANIES_LIST_FILTER_VALUES,
    };
};

export const setDealStatusConfig = (payload: any) => ({
    type: CONSTANTS.SET_DEAL_STATUS_CONFIG,
    payload,
});

export function fetchOrgInfo(payload: FetchOrgInfoPayload) {
    return {
        type: CONSTANTS.FETCH_ORG_INFO,
        payload,
    };
}

export type FetchOrgInfoPayload = {
    orgId: string;
};

export type FetchInvestorConfigPayload = {
    accountType: string;
    primaryEmail: string;
    investorOrgId: string;
    assesseeOrgId?: string;
    reportId?: string;
    userRole?: string;
    configName?: string;
};

export type FetchOrgBasedUsersPayload = {
    pageSize?: number;
    pageNumber?: number;
    orgId: string;
    userType: string[];
    orgName?: string;
    filters?: any;
    getPaginatedData?: boolean;
};

export type FetchPortfolioDataPayload = {
    portfolioTypeToFetch: string;
    pageNumber: number;
    investorOrgId: string;
    companyAccessType: string;
    partnerUserId?: string;
    companyName?: string;
    sortConfig?: SortConfig;
    filters?: any;
};

export type GetOwnerDataPayload = {
    investorOrgId: string;
};

export type InviteCompanyPayload = {
    accountType: string;
    primaryEmail: string;
    orgDisplayName: string;
    pan: string;
    journeyName: string;
    websiteUrl?: string;
    sector?: string;
    investorOrgId: string;
    phoneNumber: string;
    countryId: string;
    countryCode: string;
    // primaryUserName: string;
};
export type InviteCompanyPayloadV2 = {
    user: {
        pan: string;
        name: string;
        emailId: string;
        investorId: string;
        accountType: string;
        password: string;
        journeyName: string;
        sector?: string;
        mobileNumber: string;
        countryId: string;
        countryCode: string;
    };
    platformId: string;
};

export type InitiateReportGenerationPayload = {
    assesseeOrgId: string;
    portfolioTypeToFetch: string;
    pageNumber: number;
    investorOrgId: string;
};

export type ShowUnlockAicaAndAddToLeadsPayload = {
    showModal: boolean;
    assesseeOrgId?: string;
    orgName?: string;
    source?: UnlockAICASourcePage;
};

export type FetchTabLockStatusPayload = {
    tabsList: string[];
    assesseeOrgId: string;
};

export type OnePagerShowPayload = {
    show: boolean;
    assesseeOrgId: string;
    dealFormData?: any;
};

export const setPlatformUserData = (payload) => ({
    type: 'SET_PLATFORM_USER_DATA',
    payload,
});
